import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useWebSocket } from '@/context/WebSocketContext';
import { useLanguage } from '@/context/LanguageContext';
import api from '@/lib/api';
import {
  Monitor, Users, Buildings, SignOut, House, List, X, WifiHigh, WifiSlash,
  ArrowClockwise, FolderSimple, Terminal, MagnifyingGlass, CircleNotch,
  Desktop, Translate, Bell, MapTrifold, ClockCounterClockwise, Command, FolderOpen, CloudArrowDown, Rocket, Heartbeat, ShareNetwork, Shield, Upload
} from "@phosphor-icons/react";

const NavItem = ({ to, icon: Icon, label, isActive, onClick }) => (
  <Link to={to} onClick={onClick}
    className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${isActive ? 'bg-[#D50C2D]/10 text-[#D50C2D] border-r-2 border-[#D50C2D]' : 'text-[#a1a1aa] hover:text-white hover:bg-white/5'}`}>
    <Icon size={20} weight={isActive ? 'fill' : 'regular'} /><span>{label}</span>
  </Link>
);

const GlobalSearch = ({ autoFocus = false }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => { if (autoFocus && inputRef.current) inputRef.current.focus(); }, [autoFocus]);

  useEffect(() => {
    if (!query.trim() || query.length < 2) { setResults([]); return; }
    const timeout = setTimeout(async () => {
      setLoading(true);
      try { const res = await api.get('/search', { params: { q: query } }); setResults(res.data.results || []); }
      catch (e) { setResults([]); }
      finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(timeout);
  }, [query]);

  const handleSelect = (result) => { navigate(result.url); setQuery(''); setOpen(false); setResults([]); };
  const typeIcons = { device: Desktop, tenant: Buildings, group: FolderSimple };

  return (
    <div className="relative" ref={ref} data-testid="global-search">
      <div className="relative">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52525b]" />
        <input ref={inputRef} type="text" value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => { if (query.length >= 2) setOpen(true); }}
          placeholder={t('search.placeholder')}
          data-testid="global-search-input"
          className="w-full sm:w-[300px] lg:w-[360px] h-9 pl-9 pr-16 bg-[#0A0A0B] border border-white/10 rounded text-sm text-white placeholder:text-[#52525b] focus:outline-none focus:border-[#D50C2D] transition-colors" />
        <kbd className="absolute right-3 top-1/2 -translate-y-1/2 hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-mono text-[#52525b] bg-white/5 border border-white/10 rounded">
          <Command size={10} />K
        </kbd>
        {loading && <CircleNotch size={14} className="absolute right-12 top-1/2 -translate-y-1/2 animate-spin text-[#D50C2D]" />}
      </div>
      {open && query.length >= 2 && (
        <div className="absolute top-full mt-2 left-0 w-full sm:w-[360px] bg-[#141416] border border-white/10 rounded shadow-2xl shadow-black/50 max-h-[400px] overflow-y-auto z-50" data-testid="search-results-dropdown">
          {results.length === 0 && !loading && <div className="px-4 py-6 text-center text-[#52525b] text-sm">{t('search.noResults')}</div>}
          {results.map((r, i) => {
            const TypeIcon = typeIcons[r.type] || Desktop;
            return (
              <button key={`${r.type}-${r.id}-${i}`} onClick={() => handleSelect(r)}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors text-left border-b border-white/5 last:border-0">
                <div className="w-8 h-8 rounded bg-[#0A0A0B] flex items-center justify-center shrink-0"><TypeIcon size={16} className="text-[#a1a1aa]" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-white text-sm font-medium truncate">{r.title}</span>
                    {r.is_online !== undefined && <span className={`w-2 h-2 rounded-full shrink-0 ${r.is_online ? 'bg-green-400' : 'bg-red-400'}`} />}
                  </div>
                  {r.subtitle && <span className="text-[#52525b] text-xs block truncate">{r.subtitle}</span>}
                </div>
                <span className="text-[10px] uppercase tracking-wider text-[#52525b] px-1.5 py-0.5 rounded bg-white/5 shrink-0">
                  {r.type === 'device' ? 'Gerät' : r.type === 'tenant' ? 'Mandant' : 'Gruppe'}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const NotificationBell = () => {
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const fetchNotifs = useCallback(async () => {
    try {
      const res = await api.get('/notifications');
      setNotifs(res.data.notifications || []);
      setUnread(res.data.unread_count || 0);
    } catch (e) {}
  }, []);

  useEffect(() => { fetchNotifs(); const interval = setInterval(fetchNotifs, 30000); return () => clearInterval(interval); }, [fetchNotifs]);

  useEffect(() => {
    const handleClickOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const markAllRead = async () => {
    try { await api.patch('/notifications/read-all'); setUnread(0); setNotifs(prev => prev.map(n => ({ ...n, read: true }))); } catch (e) {}
  };

  const actionLabels = {
    device_offline: 'Gerät offline', device_online: 'Gerät online', script_failed: 'Skript fehlgeschlagen',
    device_new: 'Neues Gerät', favorite_add: 'Favorit', csv_export: 'CSV Export', bulk_import: 'Bulk Import',
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)} data-testid="notification-bell"
        className="relative p-2 text-[#a1a1aa] hover:text-white hover:bg-white/5 rounded transition-colors">
        <Bell size={18} />
        {unread > 0 && <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[#D50C2D] rounded-full text-[9px] font-bold text-white flex items-center justify-center">{unread > 9 ? '9+' : unread}</span>}
      </button>
      {open && (
        <div className="absolute top-full mt-2 right-0 w-[320px] bg-[#141416] border border-white/10 rounded shadow-2xl shadow-black/50 max-h-[400px] overflow-y-auto z-50" data-testid="notification-dropdown">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/5">
            <span className="text-white text-sm font-medium">Benachrichtigungen</span>
            {unread > 0 && <button onClick={markAllRead} className="text-xs text-[#D50C2D] hover:text-[#f21d41]">Alle gelesen</button>}
          </div>
          {notifs.length === 0 ? (
            <div className="px-4 py-8 text-center text-[#52525b] text-sm">Keine Benachrichtigungen</div>
          ) : notifs.map((n) => (
            <div key={n.id} className={`px-4 py-3 border-b border-white/5 ${!n.read ? 'bg-[#D50C2D]/5' : ''}`}>
              <div className="flex items-center gap-2">
                {!n.read && <span className="w-2 h-2 rounded-full bg-[#D50C2D] shrink-0" />}
                <span className="text-white text-sm">{n.message || actionLabels[n.action] || n.action}</span>
              </div>
              {n.details && <p className="text-[#52525b] text-xs mt-0.5 ml-4">{typeof n.details === 'string' ? n.details : JSON.stringify(n.details)}</p>}
              <span className="text-[#52525b] text-[10px] ml-4">{n.created_at ? new Date(n.created_at).toLocaleString('de-DE') : ''}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const Layout = ({ children }) => {
  const { user, logout, isSuperadmin, isAdmin } = useAuth();
  const { isConnected } = useWebSocket();
  const { language, toggleLanguage, t } = useLanguage();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const searchInputRef = useRef(null);

  // Ctrl+K shortcut
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        const input = document.querySelector('[data-testid="global-search-input"]');
        if (input) input.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const navItems = [
    { to: '/dashboard', icon: House, label: t('nav.dashboard'), show: true },
    { to: '/devices', icon: Monitor, label: t('nav.devices'), show: true },
    { to: '/map', icon: MapTrifold, label: t('nav.map') || 'Karte', show: true },
    { to: '/filemanager', icon: FolderOpen, label: t('nav.filemanager'), show: true },
    { to: '/groups', icon: FolderSimple, label: t('nav.groups'), show: true },
    { to: '/scripts', icon: Terminal, label: t('nav.scripts'), show: isAdmin },
    { to: '/deployments', icon: Rocket, label: 'Deployments', show: isAdmin },
    { to: '/agent-update', icon: CloudArrowDown, label: 'Agent Updates', show: isAdmin },
    { to: '/health', icon: Heartbeat, ShareNetwork, label: 'Health Center', show: isAdmin },
    { to: '/distribution', icon: ShareNetwork, label: 'Distribution', show: isAdmin },
    { to: '/kiosk', icon: Shield, label: 'Device Mode', show: isAdmin },
    { to: '/uploads', icon: Upload, label: 'Uploads', show: isAdmin },
    { to: '/install-status', icon: ArrowClockwise, label: t('nav.installStatus'), show: isAdmin },
    { to: '/audit-log', icon: ClockCounterClockwise, label: t('nav.auditLog') || 'Audit Log', show: isAdmin },
    { to: '/users', icon: Users, label: t('nav.users'), show: isAdmin },
    { to: '/tenants', icon: Buildings, label: t('nav.tenants'), show: isSuperadmin },
  ];

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <div className="app-layout">
      {mobileMenuOpen && <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={closeMobileMenu} />}
      <aside className={`sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="flex items-center justify-between px-5 py-5 border-b border-white/10">
          <Link to="/dashboard" className="flex items-center gap-3 hover:opacity-80 transition-opacity" onClick={closeMobileMenu}>
            <div className="w-10 h-10 bg-[#D50C2D] rounded flex items-center justify-center"><Monitor size={22} weight="bold" className="text-white" /></div>
            <div><h1 className="font-heading font-bold text-white text-sm tracking-tight">TSR Remote</h1><p className="text-[10px] text-[#52525b] uppercase tracking-wider">Control System</p></div>
          </Link>
          <button onClick={closeMobileMenu} className="p-2 text-[#a1a1aa] hover:text-white hover:bg-white/5 rounded transition-colors lg:hidden"><X size={20} /></button>
        </div>
        <nav className="flex-1 py-4">
          {navItems.filter(item => item.show).map((item) => (
            <NavItem key={item.to} to={item.to} icon={item.icon} label={item.label}
              isActive={location.pathname === item.to || (item.to !== '/dashboard' && location.pathname.startsWith(item.to))} onClick={closeMobileMenu} />
          ))}
        </nav>
        <div className="border-t border-white/10 p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded bg-[#1C1C1F] flex items-center justify-center text-[#D50C2D] font-heading font-bold text-sm">{user?.name?.charAt(0)?.toUpperCase() || 'U'}</div>
            <div className="flex-1 min-w-0"><p className="text-sm font-medium text-white truncate">{user?.name}</p><p className="text-xs text-[#52525b] truncate">{user?.tenant_name || 'TSR'}</p></div>
          </div>
          <div className="flex items-center justify-between">
            <span className={`role-badge ${user?.role}`}>{user?.role}</span>
            <button onClick={logout} data-testid="logout-btn" className="p-2 text-[#a1a1aa] hover:text-white hover:bg-white/5 rounded transition-colors" title="Logout"><SignOut size={18} /></button>
          </div>
        </div>
      </aside>
      <main className="main-content">
        <header className="header">
          <div className="flex items-center gap-4">
            <button onClick={() => setMobileMenuOpen(true)} className="p-2 text-[#a1a1aa] hover:text-white hover:bg-white/5 rounded transition-colors lg:hidden"><List size={22} /></button>
            <Link to="/dashboard" className="flex items-center gap-2 lg:hidden hover:opacity-80 transition-opacity">
              <div className="w-8 h-8 bg-[#D50C2D] rounded flex items-center justify-center"><Monitor size={16} weight="bold" className="text-white" /></div>
              <span className="font-bold text-white text-sm">TSR Remote</span>
            </Link>
          </div>
          <div className="hidden sm:block"><GlobalSearch /></div>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <button onClick={toggleLanguage} data-testid="language-switcher"
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs font-medium border transition-colors bg-[#0A0A0B] border-white/10 text-[#a1a1aa] hover:text-white hover:border-white/20"
              title={language === 'de' ? 'Switch to English' : 'Auf Deutsch wechseln'}>
              <Translate size={14} /><span className="font-mono uppercase">{language === 'de' ? 'DE' : 'EN'}</span>
            </button>
            <div className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded ${isConnected ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`} data-testid="ws-status">
              {isConnected ? <WifiHigh size={14} /> : <WifiSlash size={14} />}
              <span className="font-mono hidden sm:inline">{isConnected ? t('header.live') : t('header.offline')}</span>
            </div>
          </div>
        </header>
        <div className="sm:hidden px-4 py-2 bg-[#141416] border-b border-white/10"><GlobalSearch /></div>
        <div className="p-4 sm:p-6">{children}</div>
      </main>
    </div>
  );
};

export default Layout;
